import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';

export type AssetNavbarViewMode = 'grid' | 'list';

/** Listing sort keys for asset search API `sort` parameter. */
export type ListingSortOptionId = 'a-z' | 'z-a' | 'new-to-old' | 'old-to-new';

@Component({
  selector: 'app-asset-navbar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './asset-navbar.component.html',
  styleUrl: './asset-navbar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssetNavbarComponent {
  @ViewChild('sortTrigger', { read: ElementRef }) sortTrigger?: ElementRef<HTMLElement>;
  @ViewChild('sortList', { read: ElementRef }) sortList?: ElementRef<HTMLElement>;

  @Input() searchPlaceholder = 'Search across 364 datasets…';
  @Input() searchQuery = '';
  @Input() totalCount = 0;
  @Input() showingFrom = 1;
  @Input() showingTo = 6;
  @Input() sortOption: ListingSortOptionId | string = 'new-to-old';
  @Input() viewMode: AssetNavbarViewMode = 'grid';
  @Input() bookmarksActive = false;
  @Input() likeActive = false;
  @Input() isLoggedIn = false;

  @Output() searchChange = new EventEmitter<string>();
  @Output() bookmarksClick = new EventEmitter<void>();
  @Output() likesClick = new EventEmitter<void>();
  @Output() sortChange = new EventEmitter<ListingSortOptionId>();
  @Output() viewModeChange = new EventEmitter<AssetNavbarViewMode>();

  readonly sortDefinitions: ReadonlyArray<{ id: ListingSortOptionId; label: string }> = [
    { id: 'a-z', label: 'A-Z' },
    { id: 'z-a', label: 'Z-A' },
    { id: 'new-to-old', label: 'New to Old' },
    { id: 'old-to-new', label: 'Old to New' },
  ];

  readonly sortMenuOpen = signal(false);
  readonly sortListLayout = signal<{ top: string; left: string; width: string } | null>(null);

  sortLabel(): string {
    const id = this.sortOption as ListingSortOptionId;
    return this.sortDefinitions.find((d) => d.id === id)?.label ?? 'New to Old';
  }

  isSelected(id: ListingSortOptionId): boolean {
    return (this.sortOption as ListingSortOptionId) === id;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.sortMenuOpen()) return;
    const t = event.target as Node;
    if (this.sortTrigger?.nativeElement?.contains(t)) return;
    if (this.sortList?.nativeElement?.contains(t)) return;
    this.closeSortMenu();
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape' || !this.sortMenuOpen()) return;
    this.closeSortMenu();
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (this.sortMenuOpen()) {
      this.updateSortListPosition();
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.sortMenuOpen()) {
      this.updateSortListPosition();
    }
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchChange.emit(value);
  }

  toggleSortMenu(event: Event): void {
    event.stopPropagation();
    if (this.sortMenuOpen()) {
      this.closeSortMenu();
      return;
    }
    this.sortMenuOpen.set(true);
    setTimeout(() => {
      if (!this.sortMenuOpen()) return;
      this.updateSortListPosition();
    }, 0);
  }

  selectSortOption(id: ListingSortOptionId, event: Event): void {
    event.stopPropagation();
    this.sortChange.emit(id);
    this.closeSortMenu();
  }

  setViewMode(mode: AssetNavbarViewMode): void {
    if (this.viewMode === mode) return;
    this.viewModeChange.emit(mode);
  }

  private closeSortMenu(): void {
    this.sortMenuOpen.set(false);
    this.sortListLayout.set(null);
  }

  private updateSortListPosition(): void {
    const el = this.sortTrigger?.nativeElement;
    if (!el) return;
    const r = el.getBoundingClientRect();
    this.sortListLayout.set({
      top: `${Math.round(r.bottom + 4)}px`,
      left: `${Math.round(r.left)}px`,
      width: `${Math.round(Math.max(r.width, 200))}px`,
    });
  }
}
